// src/scenes/ProfileLoadingScene.js
import Phaser from 'phaser';
import { auth, db, firebase } from "../firebaseConfig.js";
import { getPlayerBaseline, getPlayerLevelBests, calculatePlayerImprovement, getRecentSessionStats, getRecentLevelPerformances } from "../services/analyticsService.js";
import sceneManager from "../utils/SceneManager.js";
import BaseMenuScene from "./BaseMenuScene.js";

export default class ProfileLoadingScene extends BaseMenuScene {
    constructor() {
        super({ key: "ProfileLoadingScene" });
    }

    create() {
        super.create(); // Creates the standard background
        this.add.text(this.cameras.main.width / 2, this.cameras.main.height / 2, "Loading profile data...", {
            fontFamily: "monospace",
            fontSize: "24px",
            fill: "#00FF00"
        }).setOrigin(0.5);

        this.loadDataAndProceed();
    }

    async loadDataAndProceed() {
        try {
            const loadedData = await this.loadAllData();
            
            if (this.scene.isActive(this.scene.key)) {
                // Feature flag to use DOM-based Profile Scene
                const useDOMProfile = localStorage.getItem('pptHeroUseDOMProfile') === 'true';
                const targetScene = useDOMProfile ? 'ProfileSceneDOM' : 'ProfileScene';
                sceneManager.switchToScene(targetScene, { loadedData });
            }
        } catch (error) {
            console.error("Fatal error during profile data loading:", error);
            if (window.showGeneralMessage) {
                window.showGeneralMessage("Could not load profile. Returning to menu.", 4000, true);
            }
            if (this.scene.isActive(this.scene.key)) {
                const useDOMAuthMenu = localStorage.getItem('pptHeroUseDOMAuthMenu') === 'true';
                sceneManager.switchToScene(useDOMAuthMenu ? "AuthenticatedMenuSceneDOM" : "AuthenticatedMenuScene");
            }
        }
    }

    async loadAllData() {
        const user = auth.currentUser;
        if (!user) throw new Error("User not authenticated.");

        const loadedData = {
            profileData: {},
            pptScores: [], shortcutScores: [], classifyTechScores: [],
            pptGamesPlayed: 0, shortcutGamesPlayed: 0, classifyTechGamesPlayed: 0,
            pptBestScore: 0, shortcutBestScore: 0, classifyTechBestScore: 0,
            pptHighestLevel: 1, shortcutHighestLevel: 1,
            pptPlayerRank: "Not ranked", shortcutPlayerRank: "Not ranked", classifyTechPlayerRank: "Not ranked"
        };

        const profileDoc = await db.collection("userProfiles").doc(user.uid).get();
        loadedData.profileData = profileDoc.exists ? profileDoc.data() : { displayName: user.displayName || user.email, email: user.email, createdAt: firebase.firestore.Timestamp.now() };

        [loadedData.baselinePPT, loadedData.levelBestsPPT, loadedData.improvementPPT, loadedData.recentSessionStatsPPT, loadedData.recentPerformancesPPT] = await Promise.all([
            getPlayerBaseline(user.uid).catch(e => null),
            getPlayerLevelBests(user.uid, "PPTHero").catch(e => []),
            calculatePlayerImprovement(user.uid, "PPTHero").catch(e => null),
            getRecentSessionStats(user.uid, "PPTHero").catch(e => null),
            getRecentLevelPerformances(user.uid, "PPTHero").catch(e => [])
        ]);

        if (loadedData.levelBestsPPT?.length > 0) {
            const highestLevel = Math.max(...loadedData.levelBestsPPT.filter(l => l && typeof l.levelNumber === 'number').map(l => l.levelNumber));
            loadedData.pptHighestLevel = highestLevel > 0 ? highestLevel : 1;
        }

        await this.loadScores(user.uid, loadedData);
        this.calculateRanks(user.uid, loadedData);
        
        return loadedData;
    }

    async loadScores(userId, data) {
        try {
            const gameTypes = ["PPTHero", "Shortcut", "ClassifyTech"];
            const scorePromises = gameTypes.map(gameType => {
                let queryGameTypes = (gameType === "Shortcut") ? ["Shortcut", "slide wars", "Slide Wars", "MultiplayerShortcut"] : [gameType];
                return db.collection("scores").where("game", "in", queryGameTypes).orderBy("score", "desc").limit(10).get();
            });
            const userScorePromises = gameTypes.map(gameType => {
                let queryGameTypes = (gameType === "Shortcut") ? ["Shortcut", "slide wars", "Slide Wars", "MultiplayerShortcut"] : [gameType];
                return db.collection("scores").where("userUID", "==", userId).where("game", "in", queryGameTypes).get();
            });

            const [pptSnap, shortcutSnap, classifySnap, userPptSnap, userScSnap, userClassifySnap] = await Promise.all([...scorePromises, ...userScorePromises]);

            pptSnap.forEach(d => data.pptScores.push(d.data()));
            shortcutSnap.forEach(d => data.shortcutScores.push(d.data()));
            classifySnap.forEach(d => data.classifyTechScores.push(d.data()));

            data.pptGamesPlayed = userPptSnap.size;
            userPptSnap.forEach(doc => { if (doc.data().score > data.pptBestScore) data.pptBestScore = doc.data().score; });

            data.shortcutGamesPlayed = userScSnap.size;
            userScSnap.forEach(doc => { if (doc.data().score > data.shortcutBestScore) data.shortcutBestScore = doc.data().score; });

            data.classifyTechGamesPlayed = userClassifySnap.size;
            userClassifySnap.forEach(doc => { if (doc.data().score > data.classifyTechBestScore) data.classifyTechBestScore = doc.data().score; });

            const localScores = JSON.parse(localStorage.getItem("localScores") || "[]").filter(s => !s.expiresAt || s.expiresAt > Date.now());
            localScores.forEach(s => {
                if (s.game === "PPTHero" && s.score > data.pptBestScore) data.pptBestScore = s.score;
                else if (["Shortcut", "Slide Wars"].includes(s.game) && s.score > data.shortcutBestScore) data.shortcutBestScore = s.score;
                else if (s.game === "ClassifyTech" && s.score > data.classifyTechBestScore) data.classifyTechBestScore = s.score;
            });
        } catch (err) { console.warn("Error loading scores:", err); }
    }

    calculateRanks(userId, data) {
        const calculateRankForGame = (scoresArray, bestScore) => {
            if (scoresArray.length === 0 && bestScore > 0) return "Not ranked";
            if (scoresArray.length === 0) return "N/A";
            const userIndex = scoresArray.findIndex(s => s.userUID === userId);
            return userIndex !== -1 ? `#${userIndex + 1}` : "Not ranked";
        };
        data.pptPlayerRank = calculateRankForGame(data.pptScores, data.pptBestScore);
        data.shortcutPlayerRank = calculateRankForGame(data.shortcutScores, data.shortcutBestScore);
        data.classifyTechPlayerRank = calculateRankForGame(data.classifyTechScores, data.classifyTechBestScore);
    }
}